import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { FollowUpItem, UpdateFollowUpInput } from '@bunker/shared';

/**
 * Loads + manages the follow-ups inbox ("Напоминания"). Fetches lazily when the
 * inbox is `active` (the modal is open); mutations patch local state from the
 * server's echo so the list updates without a refetch.
 */
/**
 * @param onError optional callback the host surfaces (a toast) when an
 *   update/remove is rejected by the server. The list is reconciled via reload;
 *   this stops the failure being silent (update used to reject unhandled, remove
 *   used to silently restore the row).
 */
export function useFollowUps(active: boolean, onError?: (err: unknown) => void) {
  const [items, setItems] = useState<FollowUpItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listFollowUps();
      // A reply without `items` (proxy error page, stub) is an empty inbox —
      // `undefined` here crashed the whole «Напоминания» screen.
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) void reload();
  }, [active, reload]);

  const update = useCallback(async (id: string, input: UpdateFollowUpInput) => {
    try {
      const updated = await api.updateFollowUp(id, input);
      setItems((prev) => prev.map((f) => (f.id === id ? updated : f)));
    } catch (e) {
      void reload();
      onError?.(e);
    }
  }, [reload, onError]);

  const remove = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((f) => f.id !== id));
    try {
      await api.deleteFollowUp(id);
    } catch (e) {
      void reload();
      onError?.(e);
    }
  }, [reload, onError]);

  return { items, loading, error, reload, update, remove };
}
